import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
// import { ReactComponent as FyorinLogo } from "./assets/fyorin-logo.svg";
import { Actions as COMPANY_ACTIONS } from "../../../store/actions/companyActions";
import { Actions as PAYMENT_INTEGRATION_ACTIONS } from "./store/paymentIntegrationActions";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import Spinner from "@/common/Spinner";
import { ActionListOption } from "@/common/custom-selectors/ActionList";
import { camelCase } from "lodash";
import { toTitleCase } from "@/common/validation/Validators";

const PayWith = ({
	period,
	userPermissions,
	setBankTransferModal,
	setBankTransferPeriod,
	integration,
	status,
	sending,
	getPayPeriodStatus,
	largeButton,
	availablePaymentIntegrations,
	action,
	integrationDetails,
	connected,
}) => {
	const { summary, id: periodId, complete } = period || {};
	const openBankTransfer = (integration) => {
		setBankTransferModal(integration);
		setBankTransferPeriod(period);
	};
	const Icon = useMemo(() => {
		if (sending || status === "pending")
			return (
				<Spinner
					style={{ color: "green", width: 12, position: "relative", top: -8, right: -14 }}
					className="animate-spin -ml-1 mr-3"
				/>
			);
		if (status === "success")
			return <CheckCircleIcon style={{ color: "green", width: 15, position: "relative", top: -8, right: -14 }} />;
		if (status === "error")
			return <XCircleIcon style={{ color: "red", width: 15, position: "relative", top: -8, right: -14 }} />;
	}, [connected, status]);

	const canSend = !sending && (!status || status === "error") && complete;

	useEffect(() => {
		if (status !== "pending" || document.hidden) return; // if FPS status is not pending, do not check status
		const interval = setInterval(() => {
			if (status !== "pending") clearInterval(interval);
			getPayPeriodStatus(periodId);
		}, 15000); // Check Submission status every 15 seconds
		if (status !== "pending") clearInterval(interval); // if updated, clear interval

		return () => clearInterval(interval); // if component unmounted, clear interval
	}, [status]);

	const title = useMemo(() => {
		if (status) return toTitleCase(status);
		if (sending) return "Sending...";
		if (!status || status === "error") return `Send to ${integration}`;
		if (!complete) return "Payroll not complete";
	}, [status, complete]);

	if (!connected) return null;

	if (!action && largeButton)
		return (
			<button
				className="btn btn-default"
				onClick={() => {
					if (summary?.net || status === "failed") openBankTransfer();
				}}
			>
				<div style={{ height: "1.5em", width: "1.5em", float: "left", marginRight: 5 }}>
					{/* TODO replace with dynamic logo of payment provider <FyorinLogo style={{ height: "1.5em", width: "1.5em", float: "left" }} />*/}
					{Icon ? (
						<Icon
							style={{
								height: "0.75em",
								position: "relative",
								left: "-0.5em",
								bottom: "-0.6em",
							}}
						/>
					) : null}
				</div>
				<div style={{ float: "left" }}>{title}</div>
			</button>
		);

	if (!action && status) {
		return (
			<div
				title={title}
				type="button"
				complete={canSend}
				style={{
					padding: 2,
					paddingBottom: 4,
					borderRadius: "50%",
					width: 24.5,
					height: 24.5,
					cursor: canSend ? "pointer" : "not-allowed",
					marginLeft: 10,
				}}
				onClick={() => {
					if (canSend) openBankTransfer(integration);
				}}
			>
				<img
					src={integrationDetails[camelCase(integration)]?.logo}
					style={{ width: 19.5 }}
				/>
				{Icon}
			</div>
		);
	}

	if (action && !["pending", "success"].includes(status))
		return (
			<>
				{availablePaymentIntegrations.map((integration) => (
					<ActionListOption
						key={integration}
						option={{
							label: `Pay with ${integration}`,
							icon: integrationDetails[camelCase(integration)]?.logo,
							onClick: () => openBankTransfer(integration),
						}}
					/>
				))}
			</>
		);

	return null;
};

const mapStateToProps = (state, { period, periodId, integration }) => {
	// TODO Get Connected Payment Systems
	const availablePaymentIntegrations =
		state.integration?.payment &&
		Object.values(state.integration?.payment)?.reduce((prev, curr) => {
			prev.push(curr.name);
			return prev;
		}, []);

	const userPermissions = state?.permissions?.my?.permissions?.payroll == "Read";

	const integrationDetails = state.integrationDetails?.payment || {};

	const connectedIntegration = state.integration?.payment?.[camelCase(integration)];

	return {
		userPermissions,
		availablePaymentIntegrations: availablePaymentIntegrations,
		status: connectedIntegration?.payPeriods?.find(({ payPeriodId }) => payPeriodId === periodId)?.status,
		sending: connectedIntegration?.sendingPayroll?.includes(periodId),
		integrationDetails,
		connected: connectedIntegration?.companyId,
	};
};

const mapDispatchToProps = (dispatch, { integration }) => ({
	setBankTransferPeriod: (period) => dispatch(COMPANY_ACTIONS.setBankTransferPeriod(period)),
	setBankTransferModal: () => dispatch(COMPANY_ACTIONS.setBankTransferModal(camelCase(integration))),
	getPayPeriodStatus: (payPeriodId) =>
		dispatch(PAYMENT_INTEGRATION_ACTIONS.getPayPeriodStatus(camelCase(integration), payPeriodId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PayWith);
